import React, { useEffect, useRef } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link,
  List,
  ListOrdered,
  Underline,
} from 'lucide-react';
import { uploadCampaignImage } from '../../services/emailCampaignService';

interface CampaignRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  rows?: number;
  businessId?: string;
}

/** Rich text editor for campaign email content (contentEditable + toolbar). */
export default function CampaignRichTextEditor({
  value,
  onChange,
  placeholder,
  rows = 6,
  businessId,
}: CampaignRichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const execCmd = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) execCmd('createLink', url);
  };

  // Upload pasted/dropped images to Storage and insert as a URL instead of a
  // huge base64 data URI (which bloats the email and breaks rendering/sending).
  const insertUploadedImage = async (file: File) => {
    if (!businessId) return false;
    const url = await uploadCampaignImage(file, businessId);
    if (!url) return false;
    editorRef.current?.focus();
    document.execCommand(
      'insertHTML',
      false,
      `<img src="${url}" alt="" style="max-width:100%;height:auto;" />`
    );
    handleInput();
    return true;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((it) => it.kind === 'file' && it.type.startsWith('image/'));
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file) {
        e.preventDefault();
        void insertUploadedImage(file);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void insertUploadedImage(file);
    }
  };

  const toolbarBtn = (active?: boolean) =>
    `p-1.5 rounded-md transition-colors touch-manipulation ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/50 flex-wrap">
        <button type="button" onClick={() => execCmd('bold')} className={toolbarBtn()}>
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('italic')} className={toolbarBtn()}>
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('underline')} className={toolbarBtn()}>
          <Underline className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <button type="button" onClick={() => execCmd('justifyLeft')} className={toolbarBtn()}>
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('justifyCenter')} className={toolbarBtn()}>
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('justifyRight')} className={toolbarBtn()}>
          <AlignRight className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <button type="button" onClick={() => execCmd('insertUnorderedList')} className={toolbarBtn()}>
          <List className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} className={toolbarBtn()}>
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={handleLink} className={toolbarBtn()}>
          <Link className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
        onDrop={handleDrop}
        data-placeholder={placeholder}
        className="px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none min-h-[120px] prose prose-sm max-w-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        style={{ minHeight: `${rows * 24}px` }}
      />
    </div>
  );
}
